/**
 * Third-Party Script Optimizer
 * Defers non-critical third-party scripts until after page interaction or idle time
 * Addresses: 464 KiB Google Tag Manager + other third-party blockers
 */

(function() {
  'use strict';

  // Configuration
  const IDLE_TIMEOUT = 3000; // 3 seconds idle before loading
  const INTERACTION_EVENTS = ['mousedown', 'touchstart', 'keydown', 'wheel'];
  const CRITICAL_ORIGINS = [
    'cdn.shopify.com',
    'fonts.shopify.com'
  ];

  // State management
  let scriptsLoaded = false;
  let interactionDetected = false;
  const deferredScripts = [];

  /**
   * Check if origin is critical and should load immediately
   */
  function isCriticalOrigin(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      return CRITICAL_ORIGINS.some(origin => urlObj.hostname.includes(origin));
    } catch (e) {
      return false;
    }
  }

  /**
   * Load deferred scripts
   */
  function loadDeferredScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    // Remove event listeners
    INTERACTION_EVENTS.forEach(event => {
      document.removeEventListener(event, handleInteraction, { passive: true });
    });

    // Process deferred scripts
    deferredScripts.forEach(scriptData => {
      const script = document.createElement('script');

      // Copy attributes
      Object.keys(scriptData.attributes).forEach(attr => {
        script.setAttribute(attr, scriptData.attributes[attr]);
      });

      // Set content or src
      if (scriptData.inline) {
        script.textContent = scriptData.content;
      }

      // Append to document
      scriptData.parent.appendChild(script);
    });

    // Clear queue
    deferredScripts.length = 0;

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('thirdpartyscriptsloaded'));
  }

  /**
   * Handle user interaction
   */
  function handleInteraction() {
    if (interactionDetected) return;
    interactionDetected = true;

    // Load scripts on next idle callback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadDeferredScripts, { timeout: 500 });
    } else {
      setTimeout(loadDeferredScripts, 0);
    }
  }

  /**
   * Intercept and defer third-party scripts
   */
  function interceptScripts() {
    // Override appendChild for script elements
    const originalAppendChild = Node.prototype.appendChild;
    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.appendChild = function(child) {
      if (child.tagName === 'SCRIPT' && shouldDeferScript(child)) {
        deferScript(child, this);
        return child;
      }
      return originalAppendChild.call(this, child);
    };

    Node.prototype.insertBefore = function(child, refChild) {
      if (child.tagName === 'SCRIPT' && shouldDeferScript(child)) {
        deferScript(child, this);
        return child;
      }
      return originalInsertBefore.call(this, child, refChild);
    };
  }

  /**
   * Check if script should be deferred
   */
  function shouldDeferScript(script) {
    // Don't defer if already loaded
    if (scriptsLoaded || interactionDetected) return false;

    // Don't defer inline scripts without src
    if (!script.src && !script.textContent) return false;

    // Don't defer critical origins
    if (script.src && isCriticalOrigin(script.src)) return false;

    // Don't defer module scripts (they're already deferred)
    if (script.type === 'module') return false;

    // Defer analytics, marketing, and heavy third-party scripts
    const src = script.src || '';
    const content = script.textContent || '';

    const deferPatterns = [
      '/gtag/js',
      'googletagmanager',
      'google-analytics',
      'facebook.net',
      'doubleclick.net',
      'redditstatic.com',
      'judge.me',
      'lenochat.com',
      'shop.app',
      'fbq(',
      'gtag(',
      'ga(',
      '_gaq'
    ];

    return deferPatterns.some(pattern =>
      src.includes(pattern) || content.includes(pattern)
    );
  }

  /**
   * Defer script loading
   */
  function deferScript(script, parent) {
    const scriptData = {
      attributes: {},
      content: script.textContent,
      inline: !script.src,
      parent: parent
    };

    // Copy attributes
    Array.from(script.attributes).forEach(attr => {
      scriptData.attributes[attr.name] = attr.value;
    });

    // Add to queue
    deferredScripts.push(scriptData);
  }

  /**
   * Initialize optimizer
   */
  function init() {
    // Intercept script insertions
    interceptScripts();

    // Listen for user interactions
    INTERACTION_EVENTS.forEach(event => {
      document.addEventListener(event, handleInteraction, { passive: true });
    });

    // Fallback: load after idle timeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        setTimeout(loadDeferredScripts, IDLE_TIMEOUT);
      });
    } else {
      setTimeout(loadDeferredScripts, IDLE_TIMEOUT + 1000);
    }

    // Load on page visibility change (when tab becomes active)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        handleInteraction();
      }
    });
  }

  // Start on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API
  window.ThirdPartyOptimizer = {
    load: loadDeferredScripts,
    isLoaded: () => scriptsLoaded
  };

})();
